import { Button } from '@base-ui/react/button';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import styled from 'styled-components';
import api, { queryClient } from '@/api';
import { Badge } from '~/components/ui/Badge';
import { Field, FieldLabel, FieldValue } from '~/components/ui/FieldDisplay';
import { PageTitle } from '~/components/ui/Page';
import { FieldGrid, Section, SectionTitle } from '~/components/ui/Section';
import buttonStyles from '~/styles/button.module.css';
import { AddGroupStudyHelperDialog } from './AddGroupStudyHelperDialog';

export const Route = createFileRoute('/_admin/groups/$groupId')({
  component: GroupDetail,
});

function GroupDetail() {
  const { groupId } = Route.useParams();
  const groupIdNum = Number(groupId);
  const [addOpen, setAddOpen] = useState(false);

  const {
    data: group,
    isLoading,
    error,
  } = api.getGroup.useQuery({
    path: { group_id: groupIdNum },
  });

  const { data: members } = api.getGroupStudyHelpers.useQuery({
    path: { group_id: groupIdNum },
    query: { limit: 50, offset: 0 },
  });

  const removeMutation = api.removeGroupStudyHelper.useMutation(undefined, {
    meta: { toast: 'Removed' },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: api.getGroupStudyHelpers.getQueryKey({
          path: { group_id: groupIdNum },
        }),
      });
    },
  });

  if (isLoading) {
    return <LoadingMessage>Loading groups...</LoadingMessage>;
  }

  if (error) {
    return <ErrorMessage>Error loading group</ErrorMessage>;
  }

  if (!group) {
    return <ErrorMessage>Group not found</ErrorMessage>;
  }

  const memberList = members?.data ?? [];
  const memberIds = memberList.map((m) => m.id);

  return (
    <Container>
      <BackLink to="/groups">&larr; Back to Groups</BackLink>
      <PageTitle>{group.name || `Group ${groupId}`}</PageTitle>

      <Section>
        <SectionTitle>Details</SectionTitle>
        <FieldGrid>
          <Field>
            <FieldLabel>ID</FieldLabel>
            <FieldValue>{group.id}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>Name</FieldLabel>
            <FieldValue>{group.name || '—'}</FieldValue>
          </Field>
        </FieldGrid>
        <LinkRow>
          <Link
            to="/study-helpers"
            search={{ group_id: groupIdNum }}
            className={buttonStyles.Button}
          >
            Study Helpers
          </Link>
        </LinkRow>
      </Section>

      <Section>
        <SectionHeader>
          <SectionTitle>Study Helper Members</SectionTitle>
          <Button
            className={buttonStyles.Button}
            onClick={() => setAddOpen(true)}
          >
            Add study helper
          </Button>
        </SectionHeader>

        {memberList.length === 0 ? (
          <EmptyMessage>No study helpers in this group yet.</EmptyMessage>
        ) : (
          <MemberList>
            {memberList.map((member) => (
              <MemberRow key={member.id}>
                <MemberInfo>
                  <MemberName>
                    <Link
                      to="/study-helpers/$studyHelperId"
                      params={{ studyHelperId: String(member.id) }}
                      style={{ color: 'var(--blue)', textDecoration: 'none' }}
                    >
                      {member.display_name}
                    </Link>
                    <Badge
                      $variant={member.role === 'admin' ? 'warning' : 'neutral'}
                    >
                      {member.role}
                    </Badge>
                  </MemberName>
                  <MemberMeta>
                    #{member.id} · {member.email} · {member.country || '—'}
                  </MemberMeta>
                </MemberInfo>
                <Button
                  className={buttonStyles.Button}
                  disabled={removeMutation.isPending}
                  onClick={() =>
                    removeMutation.mutate({
                      path: {
                        group_id: groupIdNum,
                        study_helper_id: member.id,
                      },
                    })
                  }
                >
                  Remove
                </Button>
              </MemberRow>
            ))}
          </MemberList>
        )}
      </Section>

      <AddGroupStudyHelperDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        groupId={groupIdNum}
        existingMemberIds={memberIds}
      />
    </Container>
  );
}

const Container = styled.div``;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--blue);
  text-decoration: none;
  margin-bottom: 1rem;
  font-size: 0.875rem;

  &:hover {
    text-decoration: underline;
  }
`;

const LinkRow = styled.div`
  margin-top: 1rem;
  display: flex;
  gap: 0.75rem;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.75rem;
`;

const MemberList = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid var(--grey-300);
  border-radius: 0.375rem;
  overflow: hidden;
`;

const MemberRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.75rem;
  border-bottom: 1px solid var(--grey-200);

  &:last-child {
    border-bottom: none;
  }
`;

const MemberInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const MemberName = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--grey-900);
`;

const MemberMeta = styled.div`
  font-size: 0.75rem;
  color: var(--grey-600);
`;

const EmptyMessage = styled.div`
  padding: 1rem;
  text-align: center;
  font-size: 0.875rem;
  color: var(--grey-600);
`;

const LoadingMessage = styled.div`
  padding: 2rem;
  text-align: center;
  color: var(--grey-600);
`;

const ErrorMessage = styled.div`
  padding: 2rem;
  text-align: center;
  color: var(--error);
`;
